import { z } from 'zod';
import { APIFY_SCRAPE_PRESETS } from '../scraper/apifyPresets.js';
import { SCRAPE_FETCH_ENGINES } from '../scraper/scrapeEngine.js';
export const scrapeSegmentationSchema = z.object({
    niche: z.string().max(200).optional().nullable(),
    region: z.string().max(200).optional().nullable(),
    queries: z.array(z.string().max(500)).max(20).optional(),
});
export const apifyRunConfigSchema = z.object({
    actor_id: z.string().min(3).max(200).optional(),
    input: z.record(z.string(), z.unknown()).optional(),
    max_items: z.number().int().min(1).max(5000).default(200),
    timeout_secs: z.number().int().min(60).max(3600).default(900),
});
/** Post-import website crawl for e-mail (SC-E6, Apify jobs). */
export const websiteEnrichConfigSchema = z.object({
    enabled: z.boolean().default(true),
    engine: z.enum(SCRAPE_FETCH_ENGINES).default('fetch'),
    max_pages_per_site: z.number().int().min(1).max(10).default(3),
    path_templates: z.array(z.string().max(200)).max(20).optional(),
});
export const SCRAPE_JOB_SOURCES = ['urls', 'apify', 'google_places', 'google_serp'];
export const googlePlacesRunConfigSchema = z.object({
    max_items: z.number().int().min(1).max(500).default(200),
    language_code: z.string().min(2).max(10).default('nl'),
    region_code: z.string().min(2).max(10).default('NL'),
});
/** Google Programmable Search / Custom Search JSON API — keyword SERP */
export const googleSerpRunConfigSchema = z.object({
    max_items: z.number().int().min(1).max(300).default(100),
    gl: z.string().min(2).max(10).default('nl'),
    lr: z.string().min(2).max(20).default('lang_nl'),
    safe: z.enum(['active', 'off']).default('active'),
});
export const SCRAPE_JOB_STATUSES = [
    'draft',
    'queued',
    'running',
    'completed',
    'failed',
    'paused',
];
export const scrapeJobConfigSchema = z
    .object({
    source: z.enum(SCRAPE_JOB_SOURCES).default('urls'),
    seed_urls: z.array(z.string().url().max(2000)).max(50).default([]),
    notes: z.string().max(2000).optional(),
    /** Apify Store preset — Maps of Google Search */
    apify_preset: z.enum(APIFY_SCRAPE_PRESETS).optional(),
    segmentation: scrapeSegmentationSchema.optional(),
    apify: apifyRunConfigSchema.optional(),
    /** Seed-URL fetch engine (SC-D) */
    engine: z.enum(SCRAPE_FETCH_ENGINES).default('fetch'),
    max_pages_per_seed: z.number().int().min(1).max(20).default(1),
    /** Official Google Places API (Text Search) — aanbevolen i.p.v. Apify Maps */
    google_places: googlePlacesRunConfigSchema.optional(),
    google_serp: googleSerpRunConfigSchema.optional(),
    /** After Apify / Places import: crawl company websites for e-mail (SC-E6) */
    website_enrich: websiteEnrichConfigSchema.optional(),
})
    .superRefine((cfg, ctx) => {
    const segOk = () => {
        const q = cfg.segmentation?.queries?.length ?? 0;
        const niche = cfg.segmentation?.niche?.trim();
        const region = cfg.segmentation?.region?.trim();
        return q > 0 || Boolean(niche) || Boolean(region);
    };
    if (cfg.source === 'google_places' || cfg.source === 'google_serp') {
        if (!segOk()) {
            ctx.addIssue({
                code: 'custom',
                message: 'Segmentatie vereist: keywords/queries, niche of regio',
                path: ['segmentation'],
            });
        }
    }
    if (cfg.source === 'apify') {
        const hasPreset = Boolean(cfg.apify_preset);
        const hasActor = Boolean(cfg.apify?.actor_id && cfg.apify?.input);
        if (!hasPreset && !hasActor) {
            ctx.addIssue({
                code: 'custom',
                message: 'Apify-job vereist apify_preset of apify.actor_id + apify.input',
                path: ['apify_preset'],
            });
        }
        if (!hasActor && !segOk()) {
            ctx.addIssue({
                code: 'custom',
                message: 'Segmentatie vereist: queries, niche of regio',
                path: ['segmentation'],
            });
        }
    }
    if (cfg.source === 'urls' && cfg.seed_urls.length === 0) {
        ctx.addIssue({
            code: 'custom',
            message: 'Minimaal één seed URL',
            path: ['seed_urls'],
        });
    }
});
export const scrapeJobCreateSchema = z.object({
    project_id: z.string().uuid(),
    name: z.string().min(1).max(200),
    config: scrapeJobConfigSchema.default({
        source: 'urls',
        seed_urls: [],
        engine: 'fetch',
        max_pages_per_seed: 1,
    }),
    respect_robots: z.boolean().default(true),
    rate_limit_ms: z.number().int().min(500).max(60_000).default(2000),
    domain_allowlist: z.array(z.string().max(253)).max(100).default([]),
    audience_id: z.string().uuid().optional().nullable(),
});
export const audienceScrapeJobCreateSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    config: scrapeJobConfigSchema.default({
        source: 'urls',
        seed_urls: [],
        engine: 'fetch',
        max_pages_per_seed: 1,
    }),
    respect_robots: z.boolean().default(true),
    rate_limit_ms: z.number().int().min(500).max(60_000).default(2000),
    domain_allowlist: z.array(z.string().max(253)).max(100).default([]),
    run_after_create: z.boolean().default(false),
});
export const scrapeBlocklistSchema = z.object({
    project_id: z.string().uuid(),
    domain: z.string().min(1).max(253),
    note: z.string().max(500).optional().nullable(),
});
export const importColumnProfileSchema = z.object({
    project_id: z.string().uuid(),
    name: z.string().min(1).max(200),
    column_map: z.record(z.string(), z.string().max(100)).default({}),
});
export const EMAIL_VERIFICATION_STATUSES = ['unknown', 'valid', 'risky', 'invalid'];
export const EMAIL_VERIFICATION_LABELS = {
    unknown: 'Niet gecontroleerd',
    valid: 'E-mail OK',
    risky: 'Risico',
    invalid: 'Ongeldig',
};
//# sourceMappingURL=scraper.js.map